
import React, { useState, useEffect } from 'react'
import { Plus, Users, Clock, CheckCircle, AlertTriangle, Calendar, FileText, User, BarChart3 } from 'lucide-react'
import { getOnboardingEmployees, getOnboardingProgress } from '@/lib/real-data-service'

interface LocalOnboardingEmployee {
  id: string
  name: string
  email: string
  position: string
  department: string
  contractType: 'CLT' | 'PJ'
  startDate: string
  status: 'not_started' | 'in_progress' | 'completed'
  progress: number
  avatar?: string
  tasksCompleted: number
  totalTasks: number
  daysRemaining: number
}

// Map real onboarding employee to local interface
const mapToLocalEmployee = (employee: any): LocalOnboardingEmployee => {
  const progress = Math.round((employee.completedSteps / employee.totalSteps) * 100)
  const startDate = new Date(employee.startDate)
  const today = new Date()
  const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
  const daysRemaining = Math.max(0, 30 - daysSinceStart) // Assuming 30-day onboarding period
  
  return {
    id: employee.id,
    name: employee.name,
    email: employee.email,
    position: employee.role,
    department: employee.department,
    contractType: 'PJ', // Default, would need to be determined from real data
    startDate: employee.startDate,
    status: employee.onboardingStatus,
    progress,
    avatar: employee.name.split(' ').map((n: string) => n[0]).join(''),
    tasksCompleted: employee.completedSteps,
    totalTasks: employee.totalSteps,
    daysRemaining: employee.onboardingStatus === 'completed' ? 0 : daysRemaining
  }
}

export default function OnboardingPage() {
  const [selectedTab, setSelectedTab] = useState<'pipeline' | 'templates' | 'analytics'>('pipeline')
  const [selectedEmployee, setSelectedEmployee] = useState<LocalOnboardingEmployee | null>(null)

  // Loading states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Data states
  const [onboardingEmployees, setOnboardingEmployees] = useState<LocalOnboardingEmployee[]>([])
  const [stats, setStats] = useState({
    total: 0,
    notStarted: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0
  })

  // Load onboarding data asynchronously
  useEffect(() => {
    const loadOnboardingData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [realEmployees, realStats] = await Promise.all([
          getOnboardingEmployees(),
          getOnboardingProgress()
        ])

        const mappedEmployees = realEmployees.map(mapToLocalEmployee)
        setOnboardingEmployees(mappedEmployees)
        setStats({
          total: realStats.total,
          notStarted: realStats.notStarted,
          inProgress: realStats.inProgress,
          completed: realStats.completed,
          overdue: realStats.overdue
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load onboarding data')
        console.error('Error loading onboarding data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadOnboardingData()
  }, [])

  const getStatusBadge = (status: string) => {
    const badges = {
      'not_started': 'bg-brand-gray bg-opacity-20 text-brand-gray border-brand-gray border-opacity-40',
      'in_progress': 'bg-brand-blue bg-opacity-20 text-brand-blue border-brand-blue border-opacity-40',
      'completed': 'bg-brand-primary bg-opacity-20 text-brand-primary border-brand-primary border-opacity-40'
    }
    return badges[status as keyof typeof badges] || badges.not_started
  }

  const getStatusText = (status: string) => {
    const texts = {
      'not_started': 'Não Iniciado',
      'in_progress': 'Em Progresso',
      'completed': 'Concluído'
    }
    return texts[status as keyof typeof texts] || 'Não Iniciado'
  }

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-4"></div>
          <p className="text-white text-lg">Carregando dados de onboarding...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center py-12">
          <div className="text-red-400 text-lg mb-2">Erro ao carregar dados</div>
          <p className="text-brand-gray mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">Onboarding</h1>
            <p className="text-brand-gray mt-1 text-sm sm:text-base">Gerencie o processo de integração de novos colaboradores</p>
          </div>
          <button className="bg-brand-primary text-brand-dark px-4 py-3 sm:py-2 rounded flex items-center justify-center gap-2 hover:bg-opacity-90 transition-all duration-300 font-display font-medium w-full sm:w-auto">
            <Plus size={16} />
            <span>Novo Onboarding</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-brand-gray" size={20} />
            </div>
            <div className="metric-value">{stats.total}</div>
            <div className="metric-label">Total</div>
          </div>
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-brand-gray" size={20} />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-brand-gray mb-2">{stats.notStarted}</div>
            <div className="metric-label">Não Iniciados</div>
          </div>
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-brand-blue" size={20} />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-brand-blue mb-2">{stats.inProgress}</div>
            <div className="metric-label">Em Progresso</div>
          </div>
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-success" size={20} />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-success mb-2">{stats.completed}</div>
            <div className="metric-label">Concluídos</div>
          </div>
          <div className="metric-card group">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-error" size={20} />
            </div>
            <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-error mb-2">{stats.overdue}</div>
            <div className="metric-label">Atrasados</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-brand-gray border-opacity-20">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            <button
              onClick={() => setSelectedTab('pipeline')}
              className={`py-2 px-1 border-b-2 font-display font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                selectedTab === 'pipeline'
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray'
              }`}
            >
              Pipeline de Onboarding
            </button>
            <button
              onClick={() => setSelectedTab('templates')}
              className={`py-2 px-1 border-b-2 font-display font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                selectedTab === 'templates'
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray'
              }`}
            >
              Templates
            </button>
            <button
              onClick={() => setSelectedTab('analytics')}
              className={`py-2 px-1 border-b-2 font-display font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                selectedTab === 'analytics'
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray'
              }`}
            >
              Analytics
            </button>
          </nav>
        </div>

        {/* Pipeline View */}
        {selectedTab === 'pipeline' && (
          <div className="bg-brand-dark-light rounded border border-brand-gray border-opacity-20">
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                {onboardingEmployees.map((employee) => (
                  <div
                    key={employee.id}
                    className="border border-brand-gray border-opacity-20 rounded p-4 hover:border-brand-primary hover:border-opacity-40 transition-all duration-300 cursor-pointer"
                    onClick={() => setSelectedEmployee(employee)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-brand-primary bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-brand-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display font-semibold text-white truncate">{employee.name}</h3>
                          <p className="text-sm text-brand-gray truncate">{employee.position} • {employee.department}</p>
                          <p className="text-xs text-brand-gray opacity-75">Início: {new Date(employee.startDate).toLocaleDateString('pt-BR')}</p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        <div className="flex items-center space-x-3 sm:space-x-0 sm:text-right">
                          <div className="flex-1 sm:mr-3">
                            <p className="font-display text-sm font-medium text-white">
                              {employee.tasksCompleted}/{employee.totalTasks} tarefas
                            </p>
                            <div className="w-full sm:w-32 bg-brand-dark bg-opacity-60 rounded-full h-2 mt-1">
                              <div
                                className="bg-brand-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${employee.progress}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-display font-medium border ${getStatusBadge(employee.status)}`}>
                              {getStatusText(employee.status)}
                            </span>
                            <p className="text-xs text-brand-gray opacity-75 mt-1">
                              {employee.daysRemaining > 0 ? `${employee.daysRemaining} dias restantes` : 
                               employee.daysRemaining === 0 ? 'Vence hoje' : 
                               `${Math.abs(employee.daysRemaining)} dias atrasado`}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2 justify-center sm:justify-start">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <FileText className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {selectedTab === 'templates' && (
          <div className="bg-brand-dark-light rounded border border-brand-gray border-opacity-20 p-4 sm:p-6">
            {/* Template Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* CLT Template */}
              <div className="border border-brand-gray border-opacity-20 rounded p-4 sm:p-6 bg-brand-dark bg-opacity-30">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-primary bg-opacity-20 rounded flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-white">Template CLT</h3>
                      <p className="text-sm text-brand-gray">Contrato de Trabalho</p>
                    </div>
                  </div>
                  <span className="bg-brand-primary bg-opacity-20 text-brand-primary px-2 py-1 rounded text-xs font-display font-medium self-start sm:self-center">
                    Padrão
                  </span>
                </div>
                <p className="text-brand-gray text-sm mb-4">
                  12 etapas incluindo documentação, treinamentos obrigatórios e integração com equipe.
                </p>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                  <button className="bg-brand-primary text-brand-dark px-3 py-2 rounded text-sm hover:bg-opacity-90 transition-all duration-300 font-display font-medium">
                    Editar
                  </button>
                  <button className="border border-brand-gray border-opacity-40 text-brand-gray hover:text-white hover:border-white px-3 py-2 rounded text-sm transition-all duration-300 font-display font-medium">
                    Visualizar
                  </button>
                </div>
              </div>

              {/* PJ Template */}
              <div className="border border-brand-gray border-opacity-20 rounded p-4 sm:p-6 bg-brand-dark bg-opacity-30">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0 mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-brand-blue bg-opacity-20 rounded flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-brand-blue" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-white">Template PJ</h3>
                      <p className="text-sm text-brand-gray">Pessoa Jurídica</p>
                    </div>
                  </div>
                  <span className="bg-brand-blue bg-opacity-20 text-brand-blue px-2 py-1 rounded text-xs font-display font-medium self-start sm:self-center">
                    Ativo
                  </span>
                </div>
                <p className="text-brand-gray text-sm mb-4">
                  8 etapas simplificadas focadas em documentação e apresentação da empresa.
                </p>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                  <button className="bg-brand-blue text-white px-3 py-2 rounded text-sm hover:bg-opacity-90 transition-all duration-300 font-display font-medium">
                    Editar
                  </button>
                  <button className="border border-brand-gray border-opacity-40 text-brand-gray hover:text-white hover:border-white px-3 py-2 rounded text-sm transition-all duration-300 font-display font-medium">
                    Visualizar
                  </button>
                </div>
              </div>
            </div>

            {/* Create New Template */}
            <div className="text-center py-6 sm:py-8 border-2 border-dashed border-brand-gray border-opacity-30 rounded">
              <div className="mb-4">
                <Plus className="mx-auto h-12 w-12 text-brand-gray" />
              </div>
              <h3 className="font-display text-base sm:text-lg font-medium text-white mb-2">
                Criar Novo Template
              </h3>
              <p className="text-brand-gray mb-4 sm:mb-6 max-w-md mx-auto text-sm sm:text-base px-4">
                Configure um template personalizado para diferentes tipos de contrato ou departamentos específicos.
              </p>
              <button className="bg-brand-primary text-brand-dark px-6 py-3 rounded hover:bg-opacity-90 transition-all duration-300 font-display font-medium">
                Criar Template
              </button>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {selectedTab === 'analytics' && (
          <div className="bg-brand-dark-light rounded border border-brand-gray border-opacity-20 p-4 sm:p-6">
            {/* Metrics Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-brand-dark bg-opacity-50 p-3 sm:p-4 rounded border border-brand-gray border-opacity-20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-brand-gray">Tempo Médio</p>
                    <p className="font-display text-lg sm:text-xl font-bold text-white">8.5 dias</p>
                  </div>
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-brand-primary" />
                </div>
              </div>
              
              <div className="bg-brand-dark bg-opacity-50 p-3 sm:p-4 rounded border border-brand-gray border-opacity-20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-brand-gray">Taxa de Conclusão</p>
                    <p className="font-display text-lg sm:text-xl font-bold text-brand-primary">94.2%</p>
                  </div>
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-brand-primary" />
                </div>
              </div>
              
              <div className="bg-brand-dark bg-opacity-50 p-3 sm:p-4 rounded border border-brand-gray border-opacity-20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-brand-gray">Satisfação</p>
                    <p className="font-display text-lg sm:text-xl font-bold text-brand-blue">4.7/5</p>
                  </div>
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-brand-blue" />
                </div>
              </div>
              
              <div className="bg-brand-dark bg-opacity-50 p-3 sm:p-4 rounded border border-brand-gray border-opacity-20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-brand-gray">Em Atraso</p>
                    <p className="font-display text-lg sm:text-xl font-bold text-red-400">2</p>
                  </div>
                  <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-red-400" />
                </div>
              </div>
            </div>

            {/* Charts Placeholder */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Completion Timeline */}
              <div className="bg-brand-dark bg-opacity-30 p-4 sm:p-6 rounded border border-brand-gray border-opacity-20">
                <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-4">
                  Timeline de Conclusão
                </h3>
                <div className="h-40 sm:h-48 flex items-center justify-center border-2 border-dashed border-brand-gray border-opacity-30 rounded">
                  <div className="text-center">
                    <BarChart3 className="mx-auto h-8 w-8 text-brand-gray mb-2" />
                    <p className="text-brand-gray text-sm">Gráfico de timeline será implementado</p>
                  </div>
                </div>
              </div>

              {/* Department Performance */}
              <div className="bg-brand-dark bg-opacity-30 p-4 sm:p-6 rounded border border-brand-gray border-opacity-20">
                <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-4">
                  Performance por Departamento
                </h3>
                <div className="space-y-3">
                  {[
                    { dept: 'Tecnologia', score: 95, color: 'brand-primary' },
                    { dept: 'Marketing', score: 88, color: 'brand-blue' },
                    { dept: 'Comercial', score: 92, color: 'brand-primary' },
                    { dept: 'RH', score: 97, color: 'brand-blue' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-brand-gray text-sm">{item.dept}</span>
                      <div className="flex items-center space-x-3">
                        <div className="w-16 sm:w-24 bg-brand-dark bg-opacity-60 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${item.score}%`,
                              backgroundColor: item.color === 'brand-primary' ? '#D2FA52' : '#4760E0'
                            }}
                          />
                        </div>
                        <span className="font-display text-sm font-medium text-white w-8">
                          {item.score}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
              <div>
                <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-1">
                  Relatórios Detalhados
                </h3>
                <p className="text-brand-gray text-sm">
                  Exportar relatórios completos de performance e métricas
                </p>
              </div>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <button className="bg-brand-blue text-white px-4 py-3 sm:py-2 rounded hover:bg-opacity-90 transition-all duration-300 font-display font-medium">
                  Exportar PDF
                </button>
                <button className="border border-brand-gray border-opacity-40 text-brand-gray hover:text-white hover:border-white px-4 py-3 sm:py-2 rounded transition-all duration-300 font-display font-medium">
                  Configurar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-brand-dark-light rounded max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-brand-gray border-opacity-20">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-xl sm:text-2xl font-bold text-white">Detalhes do Onboarding</h2>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="text-brand-gray hover:text-white transition-colors duration-300 text-2xl"
                >
                  ✕
                </button>
              </div>
              
              {/* Employee Info Summary */}
              <div className="bg-brand-dark bg-opacity-50 rounded p-4 mb-6 border border-brand-gray border-opacity-20">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <div className="w-16 h-16 bg-brand-primary bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-8 h-8 text-brand-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-lg sm:text-xl font-bold text-white">{selectedEmployee.name}</h3>
                    <p className="text-brand-gray">{selectedEmployee.position} • {selectedEmployee.department}</p>
                    <p className="text-sm text-brand-gray opacity-75">
                      Início: {new Date(selectedEmployee.startDate).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="text-center sm:text-right">
                    <div className="mb-2">
                      <span className="font-display text-xl sm:text-2xl font-bold text-brand-primary">{selectedEmployee.progress}%</span>
                      <p className="text-sm text-brand-gray">Concluído</p>
                    </div>
                    <div className="w-full sm:w-32 bg-brand-dark bg-opacity-60 rounded-full h-3">
                      <div
                        className="bg-brand-primary h-3 rounded-full transition-all duration-300"
                        style={{ width: `${selectedEmployee.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 mb-6">
                <button className="bg-brand-primary text-brand-dark px-4 py-3 sm:py-2 rounded hover:bg-opacity-90 transition-all duration-300 font-display font-medium flex items-center justify-center gap-2">
                  <Calendar size={16} />
                  <span>Ver Cronograma</span>
                </button>
                <button className="bg-brand-blue text-white px-4 py-3 sm:py-2 rounded hover:bg-opacity-90 transition-all duration-300 font-display font-medium flex items-center justify-center gap-2">
                  <FileText size={16} />
                  <span>Documentos</span>
                </button>
                <button className="border border-brand-gray border-opacity-40 text-brand-gray hover:text-white hover:border-white px-4 py-3 sm:py-2 rounded transition-all duration-300 font-display font-medium">
                  Editar
                </button>
              </div>

              {/* Coming Next Message */}
              <div className="text-center py-8 sm:py-12 border border-brand-gray border-opacity-20 rounded bg-brand-dark bg-opacity-30">
                <div className="mb-4">
                  <Clock className="mx-auto h-12 w-12 text-brand-gray" />
                </div>
                <h3 className="font-display text-base sm:text-lg font-medium text-white mb-2">
                  Checklist Detalhado em Desenvolvimento
                </h3>
                <p className="text-brand-gray max-w-md mx-auto text-sm sm:text-base px-4">
                  O sistema de checklist completo com rastreamento de tarefas será implementado na próxima versão da plataforma.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
} 